import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, buttonVariantClasses, cx } from "@wato/ui";
import { useApi } from "../../api/context";
import type { UpsellPromptView } from "../../api/onboarding-types";

/**
 * WP-571 contextual service-package nudge. INFORMATIONAL only: it links to the existing WP-427
 * request form and "Not now" just hides it for a cooldown — nothing here charges or creates an
 * engagement. Hidden on any load error (a nudge must never break Home).
 */
export function UpsellPrompt({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const [prompt, setPrompt] = useState<UpsellPromptView | null>(null);

  useEffect(() => {
    let live = true;
    api.getUpsellPrompt(workspaceId).then(
      (p) => live && setPrompt(p),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  const dismiss = useCallback(() => {
    if (!prompt) return;
    const key = prompt.promptKey;
    setPrompt(null);
    api.dismissUpsellPrompt(workspaceId, key).catch(() => undefined);
  }, [api, prompt, workspaceId]);

  if (!prompt) return null;
  return (
    <aside
      aria-label={t("dashboard.services.upsell.title")}
      data-testid="upsell-prompt"
      className="flex flex-col gap-2 rounded-lg border border-line bg-surface-muted p-4"
    >
      <h3 className="text-base font-semibold text-fg">{t("dashboard.services.upsell.title")}</h3>
      <p className="text-sm text-fg-muted">
        {t(`dashboard.services.upsell.${prompt.signal}` as MessageKey)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/w/${workspaceId}/settings/services?package=${encodeURIComponent(prompt.packageKey)}`}
          className={cx(
            "inline-flex h-9 items-center rounded-md px-3 text-sm font-medium",
            buttonVariantClasses.primary,
          )}
        >
          {t("dashboard.services.upsell.cta")}
        </Link>
        <Button variant="ghost" size="sm" onClick={dismiss}>
          {t("dashboard.services.upsell.dismiss")}
        </Button>
      </div>
    </aside>
  );
}
